export const KIOSK_ACCOUNT_IDLE_MS = 3 * 60_000;
export const KIOSK_ACCOUNT_WARNING_MS = 15_000;

type TimerHandle = ReturnType<typeof setTimeout>;

type KioskAccountInactivityOptions = {
  lastActivityAt: number;
  expiresAt?: number | null;
  onActivity: (at: number) => void;
  onActive: () => void;
  onWarning: () => void;
  onTimeout: () => void;
  target?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
  now?: () => number;
  setTimer?: (callback: () => void, delayMs: number) => TimerHandle;
  clearTimer?: (handle: TimerHandle | undefined) => void;
  idleMs?: number;
  warningMs?: number;
};

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel'] as const;

export function getJwtExpiresAtMs(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const parsed = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof parsed.exp === 'number' && Number.isFinite(parsed.exp) && parsed.exp >= 0
      ? parsed.exp * 1_000
      : null;
  } catch {
    return null;
  }
}

export function startKioskAccountInactivityWatcher(options: KioskAccountInactivityOptions) {
  const target = options.target ?? window;
  const now = options.now ?? Date.now;
  const setTimer = options.setTimer ?? ((callback, delayMs) => setTimeout(callback, delayMs));
  const clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
  const idleMs = options.idleMs ?? KIOSK_ACCOUNT_IDLE_MS;
  const warningMs = options.warningMs ?? KIOSK_ACCOUNT_WARNING_MS;
  let warningTimer: TimerHandle | undefined;
  let idleTimeoutTimer: TimerHandle | undefined;
  let expiryTimer: TimerHandle | undefined;
  let stopped = false;

  const clearIdleTimers = () => {
    clearTimer(warningTimer);
    clearTimer(idleTimeoutTimer);
    warningTimer = undefined;
    idleTimeoutTimer = undefined;
  };

  const clearTimers = () => {
    clearIdleTimers();
    clearTimer(expiryTimer);
    expiryTimer = undefined;
  };

  const removeActivityListeners = () => {
    ACTIVITY_EVENTS.forEach((event) => target.removeEventListener(event, recordActivity));
  };

  const terminate = () => {
    if (stopped) return;
    stopped = true;
    removeActivityListeners();
    clearTimers();
    options.onTimeout();
  };

  const scheduleIdleDeadline = (lastActivityAt: number) => {
    clearIdleTimers();
    const elapsed = Math.max(0, now() - lastActivityAt);
    const timeUntilWarning = idleMs - warningMs - elapsed;
    const timeUntilTimeout = idleMs - elapsed;

    if (timeUntilTimeout <= 0) {
      idleTimeoutTimer = setTimer(terminate, 0);
      return;
    }

    idleTimeoutTimer = setTimer(terminate, timeUntilTimeout);

    if (timeUntilWarning <= 0) {
      options.onWarning();
      return;
    }

    warningTimer = setTimer(() => {
      if (stopped) return;
      options.onWarning();
    }, timeUntilWarning);
  };

  function recordActivity(event: Event) {
    if (stopped) return;
    if (!event.isTrusted) return;

    const at = now();
    if (options.expiresAt !== null && options.expiresAt !== undefined && at >= options.expiresAt) {
      terminate();
      return;
    }

    options.onActivity(at);
    options.onActive();
    scheduleIdleDeadline(at);
  }

  ACTIVITY_EVENTS.forEach((event) =>
    target.addEventListener(event, recordActivity, { passive: true }),
  );
  scheduleIdleDeadline(options.lastActivityAt);

  if (options.expiresAt !== null && options.expiresAt !== undefined) {
    expiryTimer = setTimer(terminate, Math.max(0, options.expiresAt - now()));
  }

  return () => {
    if (!stopped) {
      stopped = true;
      removeActivityListeners();
    }
    clearTimers();
  };
}
